import {
  productDetailsService,
  productServiceWithInHouseData,
  productCatalogService,
  skuTrackerService,
  profileService,
} from 'config/api';
import { HtmlToText } from 'utils';
import { addNotification } from 'shell/actions';
import { adjustSearchParams, extendSkuInfoWithProductDetails } from '../utils';
import actionTypes from './actionTypes';

export const getProductDetails = (productId, { preSelectedColor, preSelectedSize } = {}) => async (
  dispatch,
  getState,
) => {
  const { skuInfo } = getState().productsModule?.productDetails;
  const selectedSkuId = skuInfo.metadata?.pimSkuId;
  dispatch({
    type: actionTypes.PRODUCT_DETAILS_LOADING,
  });
  try {
    const res = await productDetailsService.get(`v3/productdetails/${productId}`);
    const name = HtmlToText(res.data.name);

    const successPayload = {
      ...res.data,
      name,
    };

    // select default product sku depend on next criteria:
    let selectedSku;

    if (preSelectedColor || preSelectedSize) {
      selectedSku = res.data.skus?.find((sku) => {
        if (preSelectedColor && preSelectedSize) {
          return sku.color.name === preSelectedColor && sku.size.name === preSelectedSize;
        }

        if (preSelectedColor) {
          return sku.color.name === preSelectedColor;
        }

        if (preSelectedSize) {
          return sku.size.name === preSelectedSize;
        }
      });
    } else if (selectedSkuId) {
      selectedSku = res.data.skus?.find((sku) => sku.metadata.pimSkuId === selectedSkuId);
    }

    if (selectedSku) {
      successPayload.skuInfo = {
        ...selectedSku,
        productId,
      };
    }
    dispatch({
      type: actionTypes.PRODUCT_DETAILS_SUCCESS,
      payload: successPayload,
    });
  } catch (error) {
    dispatch({
      type: actionTypes.PRODUCT_DETAILS_ERROR,
      payload: error,
    });
  }
};

export const setSkuInfo = (skuInfo) => ({
  type: actionTypes.PRODUCT_DETAILS_SET_SKU_INFO,
  payload: skuInfo,
});

export const setExtendedSkuInfo = (skuInfo) => (dispatch, getState) => {
  const { data: productDetails } = getState().productsModule?.productDetails;

  dispatch(setSkuInfo(extendSkuInfoWithProductDetails(productDetails, skuInfo)));
};

export const getProductInventory = (skuInfo = {}) => async (dispatch) => {
  try {
    const res = await productServiceWithInHouseData.get(`?product_id=${skuInfo.metadata?.pimSkuId}`);
    const productInfo = res.data?.[0] || {};
    const webProductId =
      productInfo.webProductSku && productInfo.webProductSku.slice(0, productInfo.webProductSku.indexOf('/'));

    const webProductSku =
      productInfo.webProductSku && productInfo.webProductSku.slice(productInfo.webProductSku.indexOf('/') + 1);

    const compSku = skuInfo.id || webProductSku;
    const skuForInventory = productInfo.skuStores?.find((e) => e.skuId === compSku);

    dispatch({
      type: actionTypes.PRODUCT_DETAILS_SET_SKU_INFO,
      payload: {
        ...skuInfo,
        stockLevel: productInfo.inventory,
        expectedShipDate: productInfo.expectedShipDate || skuInfo.expectedShipDate,
        dropShip: productInfo.dropshipFlag === 'Y',
        price: {
          retailPrice: productInfo.price?.currentPrice || skuInfo.price?.retailPrice || '',
        },
        store: productInfo.store || [],
        skuInfoError: productInfo.error,
        restrictedItem: productInfo.isRestricted,
        skuStores: productInfo?.skuStores || [],
        skuInventory: skuForInventory?.storeInventories || [],
        webProductSku: webProductSku || skuInfo.id,
        webProductId: webProductId || skuInfo.productId,
        paddedInventory: productInfo.paddedInventory,
      },
    });

    return { productId: webProductId || '' };
  } catch (error) {
    dispatch({
      type: actionTypes.PRODUCT_DETAILS_SET_SKU_INFO,
      payload: skuInfo,
    });
    return {};
  }
};

export const getProductCategoriesLazy = (
  { from, size, searchValue },
  category = '',
  parentCategoryId = '',
  forceDisplayName = false,
) => async (dispatch, getState) => {
  const { data: currentData } = getState().productsModule.productCategory;
  const { brand } = getState().user.data;

  const params = { from, size, brand };

  if (category) {
    params.categoryId = category;
  }

  if (parentCategoryId) {
    params.parentCategoryId = parentCategoryId;
  }

  if (searchValue) {
    params.q = searchValue;
  }

  if (forceDisplayName) {
    params.returnAlternateName = false;
  }

  dispatch({
    type: actionTypes.PRODUCT_CATEGORY_INFINITE_LIST_LOADING,
  });

  try {
    const { data } = await productCatalogService.get('/v3/categories', { params });
    if (searchValue) {
      dispatch({
        type: actionTypes.PRODUCT_CATEGORY_SUCCESS,
        payload: data,
      });
      return;
    }

    const currentChildren = currentData && currentData[0] && currentData[0].children;
    const children = currentChildren || Array.from(Array(data[0].childrenCount));

    // merging new portion of data
    for (let i = params.from, j = 0; i <= params.from + params.size; i++, j++) {
      if (data[0].children[j]) {
        children[i] = data[0].children[j];
      }
    }

    dispatch({
      type: actionTypes.PRODUCT_CATEGORY_SUCCESS,
      payload: [
        {
          ...data[0],
          children,
        },
      ],
    });
  } catch (error) {
    dispatch({
      type: actionTypes.PRODUCT_CATEGORY_ERROR,
      payload: error,
    });
  }
};

export const getProductCategories = (isContextBrowseEnabled, category = '', parentCategoryId = '') => async (
  dispatch,
  getState,
) => {
  const { brand } = getState().user.data;
  dispatch({
    type: actionTypes.PRODUCT_CATEGORY_LOADING,
  });
  let version = 'v2/categories';
  let categorySearch = '';
  let parentCategory = '';
  if (isContextBrowseEnabled) {
    version = 'v3/categories';
  }
  if (category) {
    categorySearch = `&categoryId=${category}`;
  }
  if (parentCategoryId) {
    parentCategory = `&parentCategoryId=${parentCategoryId}`;
  }

  try {
    const res = await productCatalogService.get(`/${version}?brand=${brand}${categorySearch}${parentCategory}`);
    dispatch({
      type: actionTypes.PRODUCT_CATEGORY_SUCCESS,
      payload: res.data,
    });
  } catch (error) {
    dispatch({
      type: actionTypes.PRODUCT_CATEGORY_ERROR,
      payload: error,
    });
  }
};

/*
 * @params - params {
    categoryId,
    page,
    pageSize,
    text,
    brands,
    sizes,
    colors,
    minPrice,
    maxPrice,
    priceTypes,
    patterns,
    styles,
    occasions,
    availability,
    sortBy,
    sortDirection,
  }
 */

export const getListOfProducts = (params, isSearchByVendorEnabled) => async (dispatch) => {
  const apiVersion = isSearchByVendorEnabled ? 5 : 4;
  const apiEndpoint = productCatalogService.post(`/v${apiVersion}/product`, adjustSearchParams(params));
  if (params.page === 0) {
    dispatch({
      type: actionTypes.PRODUCT_RESET_PAGE_NUMBER,
    });
    dispatch({
      type: actionTypes.PRODUCT_LISTING_LOADING,
    });
  }
  try {
    const res = await apiEndpoint;
    dispatch({
      type: params.page === 0 ? actionTypes.PRODUCT_LISTING_SUCCESS : actionTypes.PRODUCT_LISTING_UPDATE,
      payload: {
        products: res.data?.list,
        totalCount: res.data?.totalCount || 0,
        categoryName: res.data?.categoryName || '',
      },
    });
  } catch (error) {
    dispatch({
      type: actionTypes.PRODUCT_LISTING_ERROR,
      payload: error,
    });
  }
};

export const getFilterOptions = (params, isSearchByVendorEnabled) => async (dispatch) => {
  const apiVersion = isSearchByVendorEnabled ? 5 : 4;
  const apiEndpoint = productCatalogService.post(`/v${apiVersion}/filter-options`, adjustSearchParams(params));

  dispatch({
    type: actionTypes.FILTER_OPTIONS_LOADING,
  });

  try {
    const res = await apiEndpoint;

    const filterLabels = {};
    res.data.brands.forEach((el) => {
      filterLabels[el.key] = el.title;
    });

    res.data.sizes.forEach((el) => {
      filterLabels[el.key] = el.title;
    });

    dispatch({
      type: actionTypes.SET_FILTER_LABELS,
      payload: filterLabels,
    });
    dispatch({
      type: actionTypes.FILTER_OPTIONS_SUCCESS,
      payload: res.data,
    });
  } catch (error) {
    dispatch({
      type: actionTypes.FILTER_OPTIONS_ERROR,
      payload: error,
    });
  }
};

export const resetListOfProducts = () => ({
  type: actionTypes.PRODUCT_LISTING_RESET,
});

export const productAddToBagNoProfileData = () => (dispatch) => {
  dispatch(
    addNotification({
      id: `info-${Date.now()}`,
      message: "Client doesn't have any profile",
      type: 'info',
    }),
  );

  dispatch({ type: actionTypes.PRODUCT_ADD_TO_BAG_NO_PROFILE_DATA });
};

export const setPageNumber = (pageNumber) => (dispatch) => {
  dispatch({
    type: actionTypes.PRODUCT_SET_PAGE_NUMBER,
    payload: {
      pageNumber,
    },
  });
};

export const setFilterCategory = ({ category, data }) => (dispatch) => {
  dispatch({
    type: actionTypes.SET_FILTER_CATEGORY,
    payload: {
      category,
      data,
    },
  });
};

export const setMultipleFilters = (filters) => (dispatch) => {
  Object.keys(filters).forEach((key) =>
    dispatch(
      setFilterCategory({
        category: key,
        data: filters[key],
      }),
    ),
  );
};

export const clearFilterCategory = (category, value) => (dispatch) => {
  dispatch({
    type: actionTypes.CLEAR_FILTER_CATEGORY,
    payload: { category, value },
  });
};

export const clearMultipleFilters = (filters) => (dispatch) => {
  filters.forEach((category) => dispatch(clearFilterCategory(category)));
};

export const clearAllFilters = (leaveText) => (dispatch) => {
  dispatch({
    type: actionTypes.CLEAR_ALL_FILTERS,
    payload: {
      leaveText,
    },
  });
};

export const setCategoryId = (categoryId) => ({
  type: actionTypes.SET_CATEGORY_ID,
  payload: categoryId,
});

export const addRecentProductSearch = (value) => ({
  type: actionTypes.RECENT_PRODUCT_SEARCH_ADD,
  payload: value,
});

export const clearRecentProductSearch = () => ({
  type: actionTypes.RECENT_PRODUCT_SEARCH_CLEAR,
});

export const getTrackerDetails = () => async (dispatch) => {
  dispatch({
    type: actionTypes.TRACKERS_LOADING,
  });

  try {
    const res = await skuTrackerService.get('/v1/skuTrackers');
    dispatch({
      type: actionTypes.TRACKERS_SUCCESS,
      payload: res,
    });
  } catch (error) {
    dispatch({
      type: actionTypes.TRACKERS_ERROR,
      payload: error,
    });
  }
};

export const getSkuCMDProfileMap = (cmdUriArr) => async (dispatch) => {
  dispatch({
    type: actionTypes.TRACKERS_PROFILES_LOADING,
  });

  try {
    const res = await Promise.all(
      cmdUriArr.map(async (cmdUri) => {
        const profile = await profileService.get(`profiles/${cmdUri}/info`);
        return { ...profile.data, cmdUri };
      }),
    );
    dispatch({
      type: actionTypes.TRACKERS_PROFILES_SUCCESS,
      payload: [...res],
    });
  } catch (error) {
    dispatch({
      type: actionTypes.TRACKERS_ERROR,
      payload: error,
    });
  }
};
export const getTrackerProductDetails = (skuId) => async (dispatch) => {
  dispatch({
    type: actionTypes.TRACKERS_LOADING,
  });

  try {
    const res = await skuTrackerService.get(`/v1/skuTrackers/product/${skuId}`);
    dispatch({
      type: actionTypes.TRACKERS_SET_PRODUCT_DETAILS,
      payload: res.data[0],
    });
  } catch (error) {
    dispatch({
      type: actionTypes.TRACKERS_ERROR,
      payload: error,
    });
  }
};

export const setSkuTrackerClients = (arr) => ({
  type: actionTypes.TRACKERS_SET_CLIENTS,
  payload: arr,
});

export const removeTrackerProfile = (cmdUri) => (dispatch) => {
  dispatch({
    type: actionTypes.REMOVE_TRACKER_PROFILE,
    payload: cmdUri,
  });
};

export const removeTracker = (sku) => (dispatch) => {
  dispatch({
    type: actionTypes.REMOVE_TRACKER,
    payload: sku,
  });
};

export const restoreSkuTrackerState = (skuTrackerState) => ({
  type: actionTypes.RESTORE_TRACKERS_STATE,
  payload: skuTrackerState,
});
